import type { CalendarEvent, OptionsDocument } from 'paintscout'
import React, { useEffect } from 'react'
import {
  makeStyles,
  IconButton,
  Typography,
  useClientOptions,
  useDialogs,
  type MuiTheme as Theme
} from '@paintscout/ui'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import AttachMoneyOutlinedIcon from '@material-ui/icons/AttachMoneyOutlined'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'
import LaunchIcon from '@material-ui/icons/Launch'
import ProductionEventDialog from 'src/components/dialogs/EventDialog/ProductionEventDialog'
import { goToDeal } from 'src/util/routing'
import isEmpty from 'lodash/isEmpty'
import { getQuoteTags } from '@paintscout/util/builder'
import { QuoteTagTooltip } from 'src/components/common/QuoteTagIcons'
import { getProjectTypeLabel } from './util'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1, 1, 1, 2),
      backgroundColor: '#ffffff',
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      cursor: 'grab',
      '&:hover': {
        borderColor: theme.paintscout.palette.primary
      },
      '&:hover $linkIcon': {
        display: 'block'
      },
      position: 'relative',
      transition: 'all 0.2s',
      display: 'flex',
      justifyContent: 'space-between'
    },
    cardHeaderContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      minWidth: 0
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
      padding: theme.spacing(1, 0, 1, 0),
      cursor: 'pointer',
      '&:hover': {
        color: theme.paintscout.palette.primary
      },
      '&:hover $headerText': {
        color: theme.paintscout.palette.primary
      }
    },
    headerText: {
      color: 'rgb(37, 46, 61)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    linkIcon: {
      fontSize: '1.1rem',
      display: 'none'
    },
    headerLink: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem',
      marginLeft: '-10px',
      '&:hover': {
        backgroundColor: 'transparent'
      }
    },
    visible: {
      opacity: 1
    },
    hidden: {
      opacity: 0
    },
    cardDetailsContainer: {
      marginTop: theme.spacing(0.5),
      color: 'rgb(115, 129, 152)',
      fontSize: '0.75rem',
      minWidth: 0
    },
    cardContent: {
      minWidth: 0,
      flex: 1
    },
    cardText: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    cardIcon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      flexShrink: 0
    },
    calendarIconButton: {
      border: `1px solid ${theme.paintscout.palette.primary}`,
      borderRadius: theme.shape.borderRadius,
      color: theme.paintscout.palette.primary,
      width: '28px',
      height: '28px',
      alignSelf: 'flex-end',
      '& svg': {
        fontSize: '1.25rem'
      },
      '&:hover': {
        backgroundColor: theme.paintscout.palette.primary,
        color: theme.palette.common.white
      }
    },
    addressClamp: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }),
  { name: 'UnscheduledJobCard' }
)

function UnscheduledJobCard({
  event,
  onSchedule
}: {
  event?: CalendarEvent
  onSchedule: (eventId: string, startDate: number, endDate: number, crewId: string) => void
}) {
  const classes = useStyles()
  const { openDialog, dismissDialog } = useDialogs()
  const { options } = useClientOptions()

  const eventDuration = event && event.deal?.hours ? Math.ceil(event.deal?.hours / 8) : 5

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'event',
    item: { eventId: event.id, event, duration: eventDuration, isUnscheduled: true },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const handleUnscheduledJobPreview = () => {
    const contact = event.contact ?? { id: '', firstName: event.title as string }
    openDialog(ProductionEventDialog, {
      isNew: true,
      event: { ...event, contact },
      hideCalendarLink: true,
      onCancelEvent: dismissDialog,
      onConfirm: (confirmedEvent) => {
        const start =
          confirmedEvent.start instanceof Date ? confirmedEvent.start.getTime() : (confirmedEvent.start as number)
        const end = confirmedEvent.end instanceof Date ? confirmedEvent.end.getTime() : (confirmedEvent.end as number)
        onSchedule(event.id, start, end, confirmedEvent.crew)
        dismissDialog()
      }
    })
  }

  return (
    <>
      <div
        ref={drag}
        // onClick={() => handleUnscheduledJobPreview()} // disabled for now since the button does the same thing
        className={classes.root}
        style={{ opacity: isDragging ? 0.3 : 1 }}
      >
        <div className={classes.cardContent}>
          <div className={classes.cardHeaderContainer}>
            <div
              className={classes.cardHeader}
              onClick={(e) => {
                e.stopPropagation()
                goToDeal(event.deal.id)
              }}
            >
              <Typography variant="h4" className={classes.headerText} weight={'bold'}>
                {event.title}
              </Typography>
              <LaunchIcon className={classes.linkIcon} />
            </div>
          </div>

          <div className={classes.cardDetailsContainer}>
            <div className={classes.cardText}>
              <span>{getProjectTypeLabel(event.projectType, options)}</span>
            </div>

            <div className={classes.cardText}>
              <AttachMoneyOutlinedIcon className={classes.cardIcon} />
              <span>
                {formatCurrency(event.deal.value)} ({Math.round((event.deal?.hours || 0) * 100) / 100} hrs)
              </span>
            </div>

            {event.estimator?.name && (
              <div className={classes.cardText}>
                <PersonOutlineIcon className={classes.cardIcon} />
                <span>{event.estimator?.name}</span>
              </div>
            )}

            {event.address && (
              <div className={classes.cardText}>
                <RoomOutlinedIcon className={classes.cardIcon} />
                <span className={classes.addressClamp}>{event.address}</span>
              </div>
            )}

            <EventTags tags={event.deal.tags} options={options} classes={classes} />
          </div>
        </div>

        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            handleUnscheduledJobPreview()
          }}
          color="inherit"
          className={classes.calendarIconButton}
          aria-label="View Calendar"
          size={'small'}
        >
          <CalendarTodayIcon />
        </IconButton>
      </div>
    </>
  )
}

export default UnscheduledJobCard

function EventTags(args: { tags: string[]; options: OptionsDocument; classes: ReturnType<typeof useStyles> }) {
  const { tags, options, classes } = args
  if (!tags || tags.length === 0) return null
  const tagOptions = getQuoteTags({ options })
  const fullTags = tagOptions.filter((tagOption) => tags.includes(tagOption.name))

  if (isEmpty(fullTags)) return null

  return (
    <QuoteTagTooltip tags={fullTags}>
      <div className={classes.cardText} style={{ cursor: 'default' }}>
        <LocalOfferOutlinedIcon className={classes.cardIcon} />
        <span>{`${fullTags.length} tags`}</span>
      </div>
    </QuoteTagTooltip>
  )
}
